/**
 * Admin content pages — all bundled in one file to keep the diff manageable.
 * Each exported page is registered as a child route under /admin/content in App.js.
 *
 * Uses the existing `api` axios instance (which attaches the JWT).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Plus, Trash2, Edit3, Save, X, Copy, Check, RefreshCw, Eye,
  ChevronDown, ChevronRight, ExternalLink, Upload, KeyRound,
  AlertTriangle, Zap, Loader2,
} from "lucide-react";
import "flag-icons/css/flag-icons.min.css";

const TABS = ["overview", "fees", "documents", "photo", "processing", "where_to_apply"];
const TAB_LABELS = { overview: "Overview", fees: "Fees", documents: "Documents", photo: "Photo", processing: "Processing", where_to_apply: "Where to Apply" };

// ============================================================
// DESTINATIONS
// ============================================================
export function AdminContentDestinations() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newDest, setNewDest] = useState({ code: "", name: "", flag_code: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/content/destinations");
      setList(data);
    } catch (e) { toast.error("Failed to load destinations"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/content/destinations", newDest);
      toast.success("Destination created");
      setShowNew(false); setNewDest({ code: "", name: "", flag_code: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
  };
  const remove = async (code) => {
    if (!window.confirm(`Delete destination ${code}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/content/destinations/${code}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-destinations-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-bold text-xl text-[#0f1b2d]">Destinations</h2>
        <button onClick={() => setShowNew(!showNew)} data-testid="admin-add-destination" className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-sm font-semibold px-3.5 py-2 rounded-md">
          <Plus className="w-4 h-4" /> Add destination
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="mb-6 p-4 border border-[#e2e8f0] rounded-lg bg-white grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input required placeholder="ISO code (FR)" value={newDest.code} onChange={(e) => setNewDest({ ...newDest, code: e.target.value.toUpperCase() })} className="border border-[#e2e8f0] rounded px-3 py-2 text-sm" data-testid="new-destination-code" />
          <input required placeholder="Name" value={newDest.name} onChange={(e) => setNewDest({ ...newDest, name: e.target.value })} className="border border-[#e2e8f0] rounded px-3 py-2 text-sm" data-testid="new-destination-name" />
          <input placeholder="Flag code (fr)" value={newDest.flag_code} onChange={(e) => setNewDest({ ...newDest, flag_code: e.target.value.toLowerCase() })} className="border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <button type="submit" className="flex-1 bg-[#0f1b2d] text-white text-sm font-semibold rounded px-3">Create</button>
            <button type="button" onClick={() => setShowNew(false)} className="px-3 border border-[#e2e8f0] rounded text-sm">Cancel</button>
          </div>
        </form>
      )}

      {selected ? (
        <DestinationEditor code={selected} onClose={() => { setSelected(null); load(); }} />
      ) : loading ? (
        <div className="text-sm text-[#64748b]">Loading…</div>
      ) : (
        <div className="border border-[#e2e8f0] rounded-lg bg-white overflow-hidden" data-testid="admin-destinations-list">
          <table className="w-full text-sm">
            <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold text-[#0f1b2d]">Country</th>
                <th className="text-left px-4 py-2.5 font-semibold text-[#0f1b2d]">Code</th>
                <th className="text-left px-4 py-2.5 font-semibold text-[#0f1b2d]">Published</th>
                <th className="text-left px-4 py-2.5 font-semibold text-[#0f1b2d]">Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((d) => (
                <tr key={d.code} className="border-b border-[#e2e8f0] last:border-0 hover:bg-[#f8fafc]">
                  <td className="px-4 py-3 flex items-center gap-2">
                    <span className={`fi fi-${(d.flag_code || d.code).toLowerCase()} !w-6 !h-4 rounded-sm`} />
                    <span className="font-medium text-[#0f1b2d]">{d.name}</span>
                  </td>
                  <td className="px-4 py-3 text-[#475569]">{d.code}</td>
                  <td className="px-4 py-3 text-xs">{d.is_published ? <span className="text-emerald-600 font-semibold">Published</span> : <span className="text-amber-600 font-semibold">Draft</span>}</td>
                  <td className="px-4 py-3 text-xs text-[#64748b]">{formatDate(d.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setSelected(d.code)} data-testid={`admin-edit-destination-${d.code}`} className="text-[#2563eb] text-xs font-semibold mr-3">Edit</button>
                    <button onClick={() => remove(d.code)} className="text-red-600 text-xs font-semibold">Delete</button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-[#64748b]">No destinations yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DestinationEditor({ code, onClose }) {
  const [dest, setDest] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get(`/admin/content/destinations/${code}`).then((r) => setDest(r.data)).catch(() => toast.error("Load failed"));
  }, [code]);

  const save = async () => {
    try {
      await api.patch(`/admin/content/destinations/${code}`, {
        name: dest.name,
        flag_code: dest.flag_code,
        base_content: dest.base_content || {},
        visa_type_overrides: dest.visa_type_overrides || {},
        where_to_apply_by_residence: dest.where_to_apply_by_residence || [],
        is_published: dest.is_published,
        meta_title: dest.meta_title,
        meta_description: dest.meta_description,
      });
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  if (!dest) return <div className="text-sm text-[#64748b]">Loading destination…</div>;

  return (
    <div className="border border-[#e2e8f0] rounded-lg bg-white p-6" data-testid="destination-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className={`fi fi-${(dest.flag_code || dest.code).toLowerCase()} !w-8 !h-6 rounded-sm`} />
          <div>
            <h3 className="font-display font-bold text-lg text-[#0f1b2d]">{dest.name} ({dest.code})</h3>
            <div className="text-xs text-[#64748b]">Base content — this loads on every guide page for this destination.</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="text-sm text-[#475569] px-3 py-1.5 border border-[#e2e8f0] rounded">← Back</button>
          <button onClick={save} data-testid="destination-save" className="text-sm font-semibold bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-4 py-1.5 rounded">Save</button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Name</label>
          <input value={dest.name || ""} onChange={(e) => setDest({ ...dest, name: e.target.value })} className="mt-1 w-full border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Flag code</label>
          <input value={dest.flag_code || ""} onChange={(e) => setDest({ ...dest, flag_code: e.target.value.toLowerCase() })} className="mt-1 w-full border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Published</label>
          <select value={dest.is_published ? "yes" : "no"} onChange={(e) => setDest({ ...dest, is_published: e.target.value === "yes" })} className="mt-1 w-full border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm">
            <option value="yes">Published</option><option value="no">Draft</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        <div>
          <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Meta title (SEO)</label>
          <input value={dest.meta_title || ""} onChange={(e) => setDest({ ...dest, meta_title: e.target.value })} className="mt-1 w-full border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Meta description (SEO)</label>
          <input value={dest.meta_description || ""} onChange={(e) => setDest({ ...dest, meta_description: e.target.value })} className="mt-1 w-full border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm" />
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-4">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-xs font-semibold rounded transition ${tab === t ? "bg-[#0f1b2d] text-white" : "text-[#475569] hover:bg-[#f1f5f9]"}`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
        <button
          onClick={() => setTab("__faqs__")}
          className={`px-3 py-1.5 text-xs font-semibold rounded transition ${tab === "__faqs__" ? "bg-[#0f1b2d] text-white" : "text-[#475569] hover:bg-[#f1f5f9]"}`}
        >FAQs</button>
      </div>

      <div className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-4">
        {tab === "__faqs__" ? (
          <FaqsEditor
            faqs={(dest.base_content || {}).faqs || []}
            onChange={(faqs) => setDest({ ...dest, base_content: { ...(dest.base_content || {}), faqs } })}
          />
        ) : (
          <TabJsonEditor
            tab={tab}
            value={(dest.base_content || {})[tab]}
            onChange={(v) => setDest({ ...dest, base_content: { ...(dest.base_content || {}), [tab]: v } })}
          />
        )}
      </div>

      {/* Per-residence Where to Apply entries — personalises the WTA tab on
          the combination page for users applying from a specific country. */}
      <WtaByResidenceEditor
        entries={dest.where_to_apply_by_residence || []}
        onChange={(entries) => setDest({ ...dest, where_to_apply_by_residence: entries })}
      />
    </div>
  );
}


function WtaByResidenceEditor({ entries, onChange }) {
  const add = () => onChange([...(entries || []), {
    residence_country_code: "", residence_country_name: "",
    provider: "", cities: [], booking_url: "", service_fee_note: "",
  }]);
  const update = (i, patch) => {
    const next = [...(entries || [])];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange((entries || []).filter((_, idx) => idx !== i));

  return (
    <div className="mt-8 border-t border-[#e2e8f0] pt-6" data-testid="dest-wta-residence-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-display font-bold text-base text-[#0f1b2d]">Where to Apply — per residence country</h4>
          <p className="text-xs text-[#64748b] mt-0.5">Optional. When a visitor picks a residence country, the guide page shows this provider / centre instead of the default.</p>
        </div>
        <button type="button" onClick={add} data-testid="wta-residence-add" className="text-sm font-semibold text-[#2563eb] hover:underline">+ Add residence</button>
      </div>

      {(!entries || entries.length === 0) && (
        <div className="rounded-md border border-dashed border-[#cbd5e1] bg-white px-4 py-6 text-sm text-[#64748b] text-center">
          No residence-specific entries yet. Users will see the default Where-to-Apply content.
        </div>
      )}

      <div className="space-y-3">
        {(entries || []).map((e, i) => (
          <div key={i} className="rounded-md border border-[#e2e8f0] bg-white p-3" data-testid={`wta-residence-row-${i}`}>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <input
                placeholder="Residence country code (US)"
                value={e.residence_country_code || ""}
                onChange={(ev) => update(i, { residence_country_code: ev.target.value.toUpperCase().slice(0, 8) })}
                data-testid={`wta-residence-code-${i}`}
                className="border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm"
              />
              <input
                placeholder="Country name (United States)"
                value={e.residence_country_name || ""}
                onChange={(ev) => update(i, { residence_country_name: ev.target.value })}
                data-testid={`wta-residence-name-${i}`}
                className="border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm sm:col-span-2"
              />
              <input
                placeholder="Provider (VFS Global)"
                value={e.provider || ""}
                onChange={(ev) => update(i, { provider: ev.target.value })}
                data-testid={`wta-residence-provider-${i}`}
                className="border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm"
              />
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                placeholder="Cities (comma-separated: New York, Washington DC)"
                value={(e.cities || []).join(", ")}
                onChange={(ev) => update(i, { cities: ev.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                data-testid={`wta-residence-cities-${i}`}
                className="border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm"
              />
              <input
                placeholder="Booking URL"
                value={e.booking_url || ""}
                onChange={(ev) => update(i, { booking_url: ev.target.value })}
                data-testid={`wta-residence-url-${i}`}
                className="border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm"
              />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input
                placeholder="Service fee note (optional)"
                value={e.service_fee_note || ""}
                onChange={(ev) => update(i, { service_fee_note: ev.target.value })}
                data-testid={`wta-residence-fee-${i}`}
                className="flex-1 border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm"
              />
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`wta-residence-remove-${i}`}
                className="text-xs text-red-600 hover:underline"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// COMBINATIONS
// ============================================================
export function AdminContentCombinations() {
  const [list, setList] = useState([]);
  const [nats, setNats] = useState([]);
  const [dests, setDests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newCombo, setNewCombo] = useState({ destination_code: "", nationality_code: "", status: "draft" });

  const load = async () => {
    try {
      const [c, n, d] = await Promise.all([
        api.get("/admin/content/combinations"),
        api.get("/admin/content/nationalities"),
        api.get("/admin/content/destinations"),
      ]);
      setList(c.data); setNats(n.data); setDests(d.data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/content/combinations", newCombo);
      toast.success("Created"); setShowNew(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
  };
  const remove = async (dest, nat) => {
    if (!window.confirm(`Delete combo ${dest}/${nat}?`)) return;
    await api.delete(`/admin/content/combinations/${dest}/${nat}`);
    load();
  };

  return (
    <div data-testid="admin-combinations-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-bold text-xl text-[#0f1b2d]">Combinations</h2>
        <button onClick={() => setShowNew(!showNew)} data-testid="admin-add-combination" className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-sm font-semibold px-3.5 py-2 rounded-md">
          <Plus className="w-4 h-4" /> Add combination
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="mb-6 p-4 border border-[#e2e8f0] rounded-lg bg-white grid grid-cols-1 sm:grid-cols-4 gap-3">
          <select required value={newCombo.destination_code} onChange={(e) => setNewCombo({ ...newCombo, destination_code: e.target.value })} className="border border-[#e2e8f0] rounded px-3 py-2 text-sm">
            <option value="">Destination</option>
            {dests.map((d) => <option key={d.code} value={d.code}>{d.name}</option>)}
          </select>
          <select required value={newCombo.nationality_code} onChange={(e) => setNewCombo({ ...newCombo, nationality_code: e.target.value })} className="border border-[#e2e8f0] rounded px-3 py-2 text-sm">
            <option value="">Nationality</option>
            {nats.map((n) => <option key={n.code} value={n.code}>{n.name}</option>)}
          </select>
          <select value={newCombo.status} onChange={(e) => setNewCombo({ ...newCombo, status: e.target.value })} className="border border-[#e2e8f0] rounded px-3 py-2 text-sm">
            <option value="draft">Draft</option><option value="published">Published</option>
          </select>
          <div className="flex gap-2">
            <button type="submit" className="flex-1 bg-[#0f1b2d] text-white text-sm font-semibold rounded">Create</button>
            <button type="button" onClick={() => setShowNew(false)} className="px-3 border rounded text-sm">Cancel</button>
          </div>
        </form>
      )}

      {selected ? (
        <CombinationEditor combo={selected} onClose={() => { setSelected(null); load(); }} />
      ) : (
        <div className="border border-[#e2e8f0] rounded-lg bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold text-[#0f1b2d]">Destination</th>
                <th className="text-left px-4 py-2.5 font-semibold text-[#0f1b2d]">Nationality</th>
                <th className="text-left px-4 py-2.5 font-semibold text-[#0f1b2d]">Status</th>
                <th className="text-left px-4 py-2.5 font-semibold text-[#0f1b2d]">Pending fields</th>
                <th className="text-left px-4 py-2.5 font-semibold text-[#0f1b2d]">Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={`${c.destination_code}-${c.nationality_code}`} className="border-b border-[#e2e8f0] last:border-0 hover:bg-[#f8fafc]">
                  <td className="px-4 py-3">{c.destination_code}</td>
                  <td className="px-4 py-3">{c.nationality_code}</td>
                  <td className="px-4 py-3 text-xs">{c.status === "published" ? <span className="text-emerald-600 font-semibold">Published</span> : <span className="text-amber-600 font-semibold">Draft</span>}</td>
                  <td className="px-4 py-3 text-xs">
                    {c.pending_sensitive && Object.keys(c.pending_sensitive).length > 0
                      ? <span className="text-orange-600 font-semibold">{Object.keys(c.pending_sensitive).join(", ")}</span>
                      : <span className="text-[#94a3b8]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#64748b]">{formatDate(c.last_updated)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setSelected(c)} className="text-[#2563eb] text-xs font-semibold mr-3">Edit</button>
                    <button onClick={() => remove(c.destination_code, c.nationality_code)} className="text-red-600 text-xs font-semibold">Delete</button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-[#64748b]">No combinations yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CombinationEditor({ combo, onClose }) {
  const [data, setData] = useState(null);
  const [destinationVto, setDestinationVto] = useState({}); // destination-level visa_type_overrides
  const [tab, setTab] = useState("overview");
  const [visaTypes, setVisaTypes] = useState([{ key: "tourist", name: "Tourist / Short Stay" }]);
  const [editingVisaType, setEditingVisaType] = useState("tourist");
  useEffect(() => {
    api.get(`/admin/content/combinations/${combo.destination_code}/${combo.nationality_code}`)
      .then((r) => setData(r.data)).catch(() => toast.error("Load failed"));
    api.get("/public/visa-types").then((r) => setVisaTypes(r.data.visa_types || []))
      .catch(() => {});
    // Load destination-level visa_type_overrides so we can show the effective
    // merged content for non-tourist types when the combo layer is empty.
    api.get(`/admin/content/destinations/${combo.destination_code}`)
      .then((r) => setDestinationVto(r.data?.visa_type_overrides || {}))
      .catch(() => {});
  }, [combo]);

  const isDefaultType = editingVisaType === "tourist";
  const currentTabs = isDefaultType
    ? (data?.overrides || {})
    : ((data?.visa_type_overrides || {})[editingVisaType] || {});

  // For non-tourist types the destination may provide inherited content
  // that the combo hasn't overridden. We surface it so admins can see the
  // effective content and know when they'd be overriding vs starting fresh.
  const inheritedTabs = isDefaultType ? {} : (destinationVto[editingVisaType] || {});
  const inheritedActiveTab = !isDefaultType && inheritedTabs[tab];
  const comboEmptyForActiveTab = !isDefaultType && !currentTabs[tab] && !!inheritedActiveTab;

  const setTabValue = (tabKey, val) => {
    if (isDefaultType) {
      setData({ ...data, overrides: { ...(data.overrides || {}), [tabKey]: val } });
    } else {
      const vto = { ...(data.visa_type_overrides || {}) };
      vto[editingVisaType] = { ...(vto[editingVisaType] || {}), [tabKey]: val };
      setData({ ...data, visa_type_overrides: vto });
    }
  };

  const save = async () => {
    try {
      const payload = {
        appointment_url: data.appointment_url,
        meta_title: data.meta_title,
        meta_description: data.meta_description,
        status: data.status,
      };
      // Only send the layer we edited to avoid clobbering the other one.
      if (isDefaultType) {
        payload.overrides = data.overrides || {};
      } else {
        payload.visa_type_overrides = data.visa_type_overrides || {};
      }
      await api.patch(`/admin/content/combinations/${combo.destination_code}/${combo.nationality_code}`, payload);
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  const applyPending = async () => {
    try {
      await api.post(`/admin/content/combinations/${combo.destination_code}/${combo.nationality_code}/apply-pending`);
      toast.success("Pending applied");
      // Reload
      const { data: fresh } = await api.get(`/admin/content/combinations/${combo.destination_code}/${combo.nationality_code}`);
      setData(fresh);
    } catch { toast.error("Failed to apply pending"); }
  };

  if (!data) return <div className="text-sm text-[#64748b]">Loading…</div>;
  const hasPending = data.pending_sensitive && Object.keys(data.pending_sensitive).length > 0;

  return (
    <div className="border border-[#e2e8f0] rounded-lg bg-white p-6" data-testid="combination-editor">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-lg text-[#0f1b2d]">
          {combo.destination_code} × {combo.nationality_code}
        </h3>
        <div className="flex gap-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 border border-[#e2e8f0] rounded">← Back</button>
          <button onClick={save} data-testid="combination-save" className="text-sm font-semibold bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-4 py-1.5 rounded">Save</button>
        </div>
      </div>

      {hasPending && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="flex-1 text-sm text-amber-900">
            <div className="font-semibold">Pending sensitive fields awaiting review</div>
            <div className="text-xs mt-1">Fields: {Object.keys(data.pending_sensitive).join(", ")}. These were submitted via the Content API but held back until you approve.</div>
          </div>
          <button onClick={applyPending} className="text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded">Apply now</button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div>
          <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Status</label>
          <select value={data.status || "draft"} onChange={(e) => setData({ ...data, status: e.target.value })} className="mt-1 w-full border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm">
            <option value="draft">Draft</option><option value="published">Published</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Appointment URL</label>
          <input value={data.appointment_url || ""} onChange={(e) => setData({ ...data, appointment_url: e.target.value })} className="mt-1 w-full border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Meta title</label>
          <input value={data.meta_title || ""} onChange={(e) => setData({ ...data, meta_title: e.target.value })} className="mt-1 w-full border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Meta description</label>
          <input value={data.meta_description || ""} onChange={(e) => setData({ ...data, meta_description: e.target.value })} className="mt-1 w-full border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="combination-visa-type-picker">
        <span className="text-xs uppercase text-[#64748b] font-semibold tracking-wider">Editing visa type</span>
        <select
          value={editingVisaType}
          onChange={(e) => setEditingVisaType(e.target.value)}
          data-testid="combination-visa-type-select"
          className="border border-[#e2e8f0] rounded px-2.5 py-1.5 text-sm bg-white"
        >
          {visaTypes.map((v) => (
            <option key={v.key} value={v.key}>{v.name}</option>
          ))}
        </select>
        {!isDefaultType && (
          <span className="text-xs text-[#64748b]">Fields left blank inherit from the Tourist tab.</span>
        )}
      </div>

      <div className="flex flex-wrap gap-1 mb-4">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 text-xs font-semibold rounded transition ${tab === t ? "bg-[#0f1b2d] text-white" : "text-[#475569] hover:bg-[#f1f5f9]"}`}>
            {TAB_LABELS[t]}
          </button>
        ))}
        {isDefaultType && (
          <button onClick={() => setTab("__faqs__")} className={`px-3 py-1.5 text-xs font-semibold rounded transition ${tab === "__faqs__" ? "bg-[#0f1b2d] text-white" : "text-[#475569] hover:bg-[#f1f5f9]"}`}>FAQs</button>
        )}
      </div>

      <div className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-4">
        <div className="text-xs text-[#64748b] mb-3">
          {isDefaultType
            ? "Overrides here are appended (documents) or replace (fees / processing) the destination's base content for this specific nationality."
            : `Overrides here apply only to the ${editingVisaType} visa type. Empty fields inherit from Tourist.`}
        </div>
        {tab === "__faqs__" && isDefaultType ? (
          <FaqsEditor faqs={(data.overrides || {}).faqs || data.faqs || []}
            onChange={(faqs) => setData({ ...data, overrides: { ...(data.overrides || {}), faqs } })} />
        ) : (
          <>
            {comboEmptyForActiveTab && (
              <div className="mb-3 text-xs rounded-md border border-blue-200 bg-blue-50 text-blue-900 px-3 py-2" data-testid={`combination-inherited-${editingVisaType}-${tab}`}>
                <strong>Inheriting from destination.</strong> The <em>{editingVisaType}</em> {tab} content below comes from the destination's visa-type overrides. Type here to override just for this combination.
                <details className="mt-1.5 whitespace-pre-wrap">
                  <summary className="cursor-pointer text-blue-800 hover:underline">Show inherited content</summary>
                  <pre className="mt-1 text-[11px] text-[#0f1b2d] bg-white border border-blue-100 rounded p-2 overflow-auto max-h-40">{JSON.stringify(inheritedActiveTab, null, 2)}</pre>
                </details>
              </div>
            )}
            <TabJsonEditor
              tab={tab}
              value={currentTabs[tab]}
              onChange={(v) => setTabValue(tab, v)}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// NATIONALITIES
// ============================================================
export function AdminContentNationalities() {
  const [list, setList] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [n, setN] = useState({ code: "", name: "", flag_code: "" });

  const load = () => api.get("/admin/content/nationalities").then((r) => setList(r.data)).catch(() => toast.error("Load failed"));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/content/nationalities", n); setN({ code: "", name: "", flag_code: "" }); setShowNew(false); load(); toast.success("Added"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const remove = async (code) => { if (!window.confirm(`Delete ${code}?`)) return; await api.delete(`/admin/content/nationalities/${code}`); load(); };

  return (
    <div data-testid="admin-nationalities-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-bold text-xl text-[#0f1b2d]">Nationalities</h2>
        <button onClick={() => setShowNew(!showNew)} className="inline-flex items-center gap-2 bg-[#2563eb] text-white text-sm font-semibold px-3.5 py-2 rounded"><Plus className="w-4 h-4" />Add</button>
      </div>
      {showNew && (
        <form onSubmit={create} className="mb-6 p-4 border border-[#e2e8f0] rounded-lg bg-white grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input required placeholder="Code (IN)" value={n.code} onChange={(e) => setN({ ...n, code: e.target.value.toUpperCase() })} className="border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          <input required placeholder="Name" value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} className="border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          <input placeholder="Flag code (in)" value={n.flag_code} onChange={(e) => setN({ ...n, flag_code: e.target.value.toLowerCase() })} className="border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          <button type="submit" className="bg-[#0f1b2d] text-white text-sm font-semibold rounded">Create</button>
        </form>
      )}
      <div className="border border-[#e2e8f0] rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <tr><th className="text-left px-4 py-2.5">Flag</th><th className="text-left px-4 py-2.5">Name</th><th className="text-left px-4 py-2.5">Code</th><th /></tr>
          </thead>
          <tbody>
            {list.map((x) => (
              <tr key={x.code} className="border-b border-[#e2e8f0] last:border-0">
                <td className="px-4 py-3"><span className={`fi fi-${(x.flag_code || x.code).toLowerCase()} !w-6 !h-4 rounded-sm`} /></td>
                <td className="px-4 py-3">{x.name}</td>
                <td className="px-4 py-3 text-[#64748b]">{x.code}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => remove(x.code)} className="text-red-600 text-xs font-semibold">Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// GENERIC EDITORS
// ============================================================
function TabJsonEditor({ tab, value, onChange }) {
  const [text, setText] = useState(() => JSON.stringify(value || {}, null, 2));
  const [error, setError] = useState("");
  useEffect(() => { setText(JSON.stringify(value || {}, null, 2)); }, [tab]); // eslint-disable-line

  const commit = () => {
    try {
      const parsed = text.trim() === "" ? {} : JSON.parse(text);
      onChange(parsed);
      setError("");
      toast.success("Tab content updated");
    } catch (e) { setError(String(e.message || e)); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-[#0f1b2d]">{TAB_LABELS[tab]} — structured JSON</div>
        <button onClick={commit} className="text-xs font-semibold bg-[#0f1b2d] text-white px-3 py-1 rounded">Apply</button>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={16}
        className="w-full font-mono text-xs border border-[#e2e8f0] rounded p-3 bg-white leading-5"
      />
      {error && <div className="mt-2 text-xs text-red-600">JSON error: {error}</div>}
      <div className="mt-2 text-xs text-[#64748b]">
        Tip: The shape must match the destination's base content (bullets, groups, rows, specs, etc.). See the seed data in <code>seed_content.py</code> for schema examples.
      </div>
    </div>
  );
}

function FaqsEditor({ faqs, onChange }) {
  const [items, setItems] = useState(faqs || []);
  useEffect(() => setItems(faqs || []), [faqs]);

  const update = (i, k, v) => {
    const next = items.map((it, idx) => idx === i ? { ...it, [k]: v } : it);
    setItems(next); onChange(next);
  };
  const add = () => { const next = [...items, { q: "", a: "" }]; setItems(next); onChange(next); };
  const remove = (i) => { const next = items.filter((_, idx) => idx !== i); setItems(next); onChange(next); };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-semibold text-[#0f1b2d]">FAQ items</div>
        <button onClick={add} className="text-xs font-semibold bg-[#0f1b2d] text-white px-3 py-1 rounded inline-flex items-center gap-1"><Plus className="w-3 h-3" />Add</button>
      </div>
      <div className="space-y-3">
        {items.map((f, i) => (
          <div key={i} className="rounded border border-[#e2e8f0] bg-white p-3">
            <input placeholder="Question" value={f.q} onChange={(e) => update(i, "q", e.target.value)} className="w-full border-b border-[#e2e8f0] pb-1.5 text-sm font-semibold text-[#0f1b2d]" />
            <textarea placeholder="Answer" value={f.a} onChange={(e) => update(i, "a", e.target.value)} rows={3} className="mt-2 w-full text-sm text-[#475569] border border-[#e2e8f0] rounded p-2" />
            <div className="mt-2 text-right"><button onClick={() => remove(i)} className="text-xs text-red-600">Delete</button></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// BLOG POSTS
// ============================================================
export function AdminContentPosts() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = () => api.get("/admin/content/blog/posts").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (!window.confirm("Delete this post?")) return; await api.delete(`/admin/content/blog/posts/${id}`); load(); };

  if (selected || showNew) {
    return <PostEditor postId={selected} onClose={() => { setSelected(null); setShowNew(false); load(); }} />;
  }
  return (
    <div data-testid="admin-posts-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-bold text-xl text-[#0f1b2d]">Blog posts</h2>
        <button onClick={() => setShowNew(true)} data-testid="admin-add-post" className="inline-flex items-center gap-2 bg-[#2563eb] text-white text-sm font-semibold px-3.5 py-2 rounded"><Plus className="w-4 h-4" />New post</button>
      </div>
      <div className="border border-[#e2e8f0] rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <tr>
              <th className="text-left px-4 py-2.5">Title</th>
              <th className="text-left px-4 py-2.5">Category</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-left px-4 py-2.5">Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-b border-[#e2e8f0] last:border-0 hover:bg-[#f8fafc]">
                <td className="px-4 py-3 max-w-md">
                  <div className="font-medium text-[#0f1b2d] truncate">{p.title}</div>
                  <div className="text-xs text-[#64748b]">/blog/{p.slug}</div>
                </td>
                <td className="px-4 py-3 text-xs">{p.category}</td>
                <td className="px-4 py-3 text-xs">
                  {p.status === "published" ? <span className="text-emerald-600 font-semibold">Published</span> :
                    p.status === "scheduled" ? <span className="text-blue-600 font-semibold">Scheduled</span> :
                    <span className="text-amber-600 font-semibold">Draft</span>}
                </td>
                <td className="px-4 py-3 text-xs text-[#64748b]">{formatDate(p.updated_at)}</td>
                <td className="px-4 py-3 text-right">
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-[#475569] text-xs font-semibold mr-3">View</a>
                  <button onClick={() => setSelected(p.id)} className="text-[#2563eb] text-xs font-semibold mr-3">Edit</button>
                  <button onClick={() => remove(p.id)} className="text-red-600 text-xs font-semibold">Delete</button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-[#64748b]">No posts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const EMPTY_POST = {
  title: "", slug: "", category: "document_tips", tags: [], excerpt: "",
  body: [{ type: "paragraph", text: "" }],
  featured_image: "", author_id: "", meta_title: "", meta_description: "",
  no_index: false, status: "draft", publish_at: null,
};
const CATEGORIES = [
  { id: "country_guides", label: "Country Guides" },
  { id: "document_tips", label: "Document Tips" },
  { id: "visa_news", label: "Visa News" },
  { id: "application_process", label: "Application Process" },
];

function PostEditor({ postId, onClose }) {
  const [post, setPost] = useState(postId ? null : { ...EMPTY_POST });
  const [authors, setAuthors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/content/blog/authors").then((r) => setAuthors(r.data)).catch(() => {});
    if (postId) {
      api.get(`/admin/content/blog/posts/${postId}`).then((r) => setPost(r.data)).catch(() => toast.error("Load failed"));
    }
  }, [postId]);

  if (!post) return <div className="text-sm text-[#64748b]">Loading…</div>;

  const save = async () => {
    try {
      if (postId) {
        await api.patch(`/admin/content/blog/posts/${postId}`, post);
      } else {
        await api.post("/admin/content/blog/posts", post);
      }
      toast.success("Saved");
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const uploadFeatured = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "blog");
      const { data } = await api.post("/admin/content/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Absolute URL for storage in DB — use backend URL
      const abs = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setPost({ ...post, featured_image: abs });
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  return (
    <div data-testid="post-editor">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-xl text-[#0f1b2d]">{postId ? "Edit post" : "New post"}</h2>
        <div className="flex gap-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 border rounded">← Back</button>
          <button onClick={save} data-testid="post-save" className="text-sm font-semibold bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-4 py-1.5 rounded">Save</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Title</label>
            <input value={post.title} onChange={(e) => setPost({ ...post, title: e.target.value })} data-testid="post-title" className="mt-1 w-full border border-[#e2e8f0] rounded px-3 py-2 text-lg font-display font-bold" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Slug (URL)</label>
            <input value={post.slug || ""} onChange={(e) => setPost({ ...post, slug: e.target.value })} placeholder="auto from title" className="mt-1 w-full border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Excerpt</label>
            <textarea value={post.excerpt || ""} onChange={(e) => setPost({ ...post, excerpt: e.target.value })} rows={2} className="mt-1 w-full border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1 block">Body (structured blocks)</label>
            <BlocksEditor blocks={post.body || []} onChange={(body) => setPost({ ...post, body })} />
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Status</label>
            <select value={post.status} onChange={(e) => setPost({ ...post, status: e.target.value })} data-testid="post-status" className="mt-1 w-full border border-[#e2e8f0] rounded px-3 py-2 text-sm">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </div>
          {post.status === "scheduled" && (
            <div>
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Publish at (UTC ISO)</label>
              <input value={post.publish_at || ""} onChange={(e) => setPost({ ...post, publish_at: e.target.value })} placeholder="2026-03-01T09:00:00Z" className="mt-1 w-full border border-[#e2e8f0] rounded px-3 py-2 text-sm font-mono" />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Category</label>
            <select value={post.category} onChange={(e) => setPost({ ...post, category: e.target.value })} className="mt-1 w-full border border-[#e2e8f0] rounded px-3 py-2 text-sm">
              {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Author</label>
            <select value={post.author_id || ""} onChange={(e) => setPost({ ...post, author_id: e.target.value })} className="mt-1 w-full border border-[#e2e8f0] rounded px-3 py-2 text-sm">
              <option value="">— select —</option>
              {authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Tags (comma separated)</label>
            <input value={(post.tags || []).join(", ")} onChange={(e) => setPost({ ...post, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="mt-1 w-full border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Featured image</label>
            {post.featured_image && <img src={post.featured_image} alt="preview" className="mt-2 w-full aspect-[16/9] object-cover rounded border border-[#e2e8f0]" />}
            <input ref={fileRef} type="file" accept="image/*" onChange={(e) => uploadFeatured(e.target.files?.[0])} className="hidden" />
            <div className="mt-2 flex gap-2">
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="text-xs font-semibold bg-[#0f1b2d] text-white px-3 py-1.5 rounded inline-flex items-center gap-1">
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload
              </button>
              {post.featured_image && <button onClick={() => setPost({ ...post, featured_image: "" })} className="text-xs text-red-600 font-semibold px-3 py-1.5">Remove</button>}
            </div>
          </div>
          <div className="border-t border-[#e2e8f0] pt-4">
            <div className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2">SEO</div>
            <label className="text-xs text-[#475569]">Meta title</label>
            <input value={post.meta_title || ""} onChange={(e) => setPost({ ...post, meta_title: e.target.value })} className="mt-1 w-full border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
            <label className="text-xs text-[#475569] mt-2 block">Meta description</label>
            <textarea value={post.meta_description || ""} onChange={(e) => setPost({ ...post, meta_description: e.target.value })} rows={3} className="mt-1 w-full border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
            <label className="mt-2 flex items-center gap-2 text-xs">
              <input type="checkbox" checked={!!post.no_index} onChange={(e) => setPost({ ...post, no_index: e.target.checked })} /> noindex,nofollow
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

const BLOCK_TEMPLATES = {
  heading: { type: "heading", level: 2, text: "Heading" },
  paragraph: { type: "paragraph", text: "" },
  callout: { type: "callout", variant: "info", text: "Note here" },
  key_takeaways: { type: "key_takeaways", items: [""] },
  faq: { type: "faq", items: [{ q: "", a: "" }] },
  table: { type: "table", headers: ["Col A", "Col B"], rows: [["", ""]] },
  quote: { type: "quote", text: "", cite: "" },
  code: { type: "code", text: "" },
  list: { type: "list", ordered: false, items: [""] },
  divider: { type: "divider" },
  image: { type: "image", src: "", alt: "", caption: "" },
};

function BlocksEditor({ blocks, onChange }) {
  const [expanded, setExpanded] = useState({});
  const update = (i, next) => onChange(blocks.map((b, idx) => idx === i ? next : b));
  const remove = (i) => onChange(blocks.filter((_, idx) => idx !== i));
  const move = (i, delta) => {
    const nb = [...blocks]; const t = nb[i]; const j = i + delta;
    if (j < 0 || j >= nb.length) return;
    nb[i] = nb[j]; nb[j] = t; onChange(nb);
  };
  const add = (type) => onChange([...blocks, JSON.parse(JSON.stringify(BLOCK_TEMPLATES[type]))]);

  return (
    <div>
      <div className="space-y-2">
        {blocks.map((b, i) => (
          <div key={i} className="border border-[#e2e8f0] rounded-lg bg-white">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#e2e8f0] bg-[#f8fafc]">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#0f1b2d]">{b.type}</div>
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} className="text-xs text-[#64748b]">↑</button>
                <button onClick={() => move(i, 1)} className="text-xs text-[#64748b]">↓</button>
                <button onClick={() => setExpanded({ ...expanded, [i]: !expanded[i] })} className="text-xs text-[#64748b]">{expanded[i] ? "−" : "+"}</button>
                <button onClick={() => remove(i)} className="text-xs text-red-600 ml-1">×</button>
              </div>
            </div>
            <div className="p-3">
              <BlockEditor block={b} onChange={(nb) => update(i, nb)} expanded={expanded[i]} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {Object.keys(BLOCK_TEMPLATES).map((t) => (
          <button key={t} onClick={() => add(t)} className="text-xs border border-[#e2e8f0] hover:bg-[#f1f5f9] text-[#475569] font-semibold px-2.5 py-1 rounded">
            + {t}
          </button>
        ))}
      </div>
    </div>
  );
}

function BlockEditor({ block, onChange, expanded }) {
  const set = (k, v) => onChange({ ...block, [k]: v });
  if (block.type === "heading") {
    return (
      <div className="flex gap-2 items-center">
        <select value={block.level} onChange={(e) => set("level", Number(e.target.value))} className="border border-[#e2e8f0] rounded px-2 py-1 text-xs">
          <option value={2}>H2</option><option value={3}>H3</option><option value={4}>H4</option>
        </select>
        <input value={block.text} onChange={(e) => set("text", e.target.value)} className="flex-1 border border-[#e2e8f0] rounded px-2 py-1 text-sm" />
      </div>
    );
  }
  if (block.type === "paragraph" || block.type === "code") {
    return <textarea value={block.text || ""} onChange={(e) => set("text", e.target.value)} rows={block.type === "code" ? 5 : 3} className="w-full border border-[#e2e8f0] rounded px-2 py-1.5 text-sm" placeholder={block.type === "paragraph" ? "Markdown supported…" : "Code"} />;
  }
  if (block.type === "callout") {
    return (
      <div className="space-y-2">
        <select value={block.variant} onChange={(e) => set("variant", e.target.value)} className="border border-[#e2e8f0] rounded px-2 py-1 text-xs">
          <option value="info">info</option><option value="warning">warning</option><option value="success">success</option>
        </select>
        <textarea value={block.text} onChange={(e) => set("text", e.target.value)} rows={2} className="w-full border border-[#e2e8f0] rounded px-2 py-1.5 text-sm" />
      </div>
    );
  }
  if (block.type === "key_takeaways" || block.type === "list") {
    return (
      <div className="space-y-2">
        {block.type === "list" && (
          <label className="text-xs inline-flex items-center gap-2"><input type="checkbox" checked={!!block.ordered} onChange={(e) => set("ordered", e.target.checked)} />Ordered</label>
        )}
        {(block.items || []).map((it, i) => (
          <div key={i} className="flex gap-2">
            <input value={it} onChange={(e) => set("items", block.items.map((x, j) => j === i ? e.target.value : x))} className="flex-1 border border-[#e2e8f0] rounded px-2 py-1 text-sm" />
            <button onClick={() => set("items", block.items.filter((_, j) => j !== i))} className="text-red-600 text-xs">×</button>
          </div>
        ))}
        <button onClick={() => set("items", [...(block.items || []), ""])} className="text-xs text-[#2563eb] font-semibold">+ Item</button>
      </div>
    );
  }
  if (block.type === "faq") {
    return (
      <div className="space-y-2">
        {(block.items || []).map((it, i) => (
          <div key={i} className="rounded border border-[#e2e8f0] p-2 space-y-1">
            <input placeholder="Question" value={it.q} onChange={(e) => set("items", block.items.map((x, j) => j === i ? { ...x, q: e.target.value } : x))} className="w-full border border-[#e2e8f0] rounded px-2 py-1 text-sm font-semibold" />
            <textarea placeholder="Answer" value={it.a} onChange={(e) => set("items", block.items.map((x, j) => j === i ? { ...x, a: e.target.value } : x))} rows={2} className="w-full border border-[#e2e8f0] rounded px-2 py-1 text-sm" />
            <div className="text-right"><button onClick={() => set("items", block.items.filter((_, j) => j !== i))} className="text-red-600 text-xs">Remove</button></div>
          </div>
        ))}
        <button onClick={() => set("items", [...(block.items || []), { q: "", a: "" }])} className="text-xs text-[#2563eb] font-semibold">+ FAQ item</button>
      </div>
    );
  }
  if (block.type === "table") {
    return (
      <div className="space-y-2">
        <div className="text-xs text-[#64748b]">Headers (comma):</div>
        <input value={(block.headers || []).join(", ")} onChange={(e) => set("headers", e.target.value.split(",").map((s) => s.trim()))} className="w-full border border-[#e2e8f0] rounded px-2 py-1 text-sm" />
        <div className="text-xs text-[#64748b]">Rows (one per line, comma separated cells):</div>
        <textarea
          value={(block.rows || []).map((r) => r.join(", ")).join("\n")}
          onChange={(e) => set("rows", e.target.value.split("\n").map((line) => line.split(",").map((s) => s.trim())))}
          rows={5}
          className="w-full border border-[#e2e8f0] rounded px-2 py-1 text-sm font-mono"
        />
      </div>
    );
  }
  if (block.type === "quote") {
    return (
      <div className="space-y-2">
        <textarea value={block.text} onChange={(e) => set("text", e.target.value)} rows={2} className="w-full border border-[#e2e8f0] rounded px-2 py-1.5 text-sm" placeholder="Quote text" />
        <input value={block.cite || ""} onChange={(e) => set("cite", e.target.value)} placeholder="Attribution (optional)" className="w-full border border-[#e2e8f0] rounded px-2 py-1 text-sm" />
      </div>
    );
  }
  if (block.type === "image") {
    return (
      <div className="space-y-2">
        <input value={block.src || ""} onChange={(e) => set("src", e.target.value)} placeholder="Image URL" className="w-full border border-[#e2e8f0] rounded px-2 py-1 text-sm" />
        <input value={block.alt || ""} onChange={(e) => set("alt", e.target.value)} placeholder="Alt text" className="w-full border border-[#e2e8f0] rounded px-2 py-1 text-sm" />
        <input value={block.caption || ""} onChange={(e) => set("caption", e.target.value)} placeholder="Caption (optional)" className="w-full border border-[#e2e8f0] rounded px-2 py-1 text-sm" />
      </div>
    );
  }
  if (block.type === "divider") return <div className="text-xs text-[#64748b]">— divider —</div>;
  return null;
}

// ============================================================
// AUTHORS
// ============================================================
export function AdminContentAuthors() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", title: "", bio: "", avatar_url: "" });
  const load = () => api.get("/admin/content/blog/authors").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    if (selected) await api.patch(`/admin/content/blog/authors/${selected}`, form);
    else await api.post("/admin/content/blog/authors", form);
    setSelected(null); setShowNew(false); setForm({ name: "", title: "", bio: "", avatar_url: "" });
    load(); toast.success("Saved");
  };
  const edit = (a) => { setSelected(a.id); setForm({ name: a.name, title: a.title || "", bio: a.bio || "", avatar_url: a.avatar_url || "" }); setShowNew(true); };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/content/blog/authors/${id}`); load(); };
  return (
    <div data-testid="admin-authors-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-bold text-xl text-[#0f1b2d]">Authors</h2>
        <button onClick={() => { setShowNew(true); setSelected(null); setForm({ name: "", title: "", bio: "", avatar_url: "" }); }} className="inline-flex items-center gap-2 bg-[#2563eb] text-white text-sm font-semibold px-3.5 py-2 rounded"><Plus className="w-4 h-4" />New</button>
      </div>
      {showNew && (
        <form onSubmit={save} className="mb-6 p-4 border border-[#e2e8f0] rounded-lg bg-white grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          <textarea placeholder="Bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} className="sm:col-span-2 border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          <input placeholder="Avatar URL" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} className="sm:col-span-2 border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          <div className="sm:col-span-2 flex gap-2"><button type="submit" className="bg-[#0f1b2d] text-white text-sm font-semibold px-4 py-1.5 rounded">Save</button><button type="button" onClick={() => setShowNew(false)} className="border rounded px-4 text-sm">Cancel</button></div>
        </form>
      )}
      <div className="border border-[#e2e8f0] rounded-lg bg-white overflow-hidden">
        {list.length === 0 ? <div className="p-6 text-center text-[#64748b] text-sm">No authors yet.</div> : list.map((a) => (
          <div key={a.id} className="flex items-center justify-between px-4 py-3 border-b border-[#e2e8f0] last:border-0">
            <div>
              <div className="font-medium text-[#0f1b2d]">{a.name}</div>
              <div className="text-xs text-[#64748b]">{a.title}</div>
            </div>
            <div>
              <button onClick={() => edit(a)} className="text-[#2563eb] text-xs font-semibold mr-3">Edit</button>
              <button onClick={() => remove(a.id)} className="text-red-600 text-xs font-semibold">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// API KEYS, WEBHOOKS, SETTINGS, REVIEW QUEUE, ACTIVITY
// ============================================================
export function AdminContentApiKeys() {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState(null);
  const load = () => api.get("/admin/content/api-keys").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const { data } = await api.post("/admin/content/api-keys", { name });
    setNewKey(data); setName(""); load();
  };
  const revoke = async (id) => { if (!window.confirm("Revoke this key? Anyone using it will lose access immediately.")) return; await api.delete(`/admin/content/api-keys/${id}`); load(); };
  const copyKey = (k) => { navigator.clipboard.writeText(k); toast.success("Key copied"); };
  return (
    <div data-testid="admin-apikeys-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-bold text-xl text-[#0f1b2d]">Content API keys</h2>
      </div>
      <div className="mb-6 rounded-lg border border-[#e2e8f0] bg-white p-4">
        <div className="text-xs text-[#64748b] mb-2">Point Make.com or any AI agent at these endpoints with the <code>X-API-Key</code> header set to the key value below.</div>
        <form onSubmit={create} className="flex gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Key label (e.g. 'Make.com production')" className="flex-1 border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          <button type="submit" className="bg-[#2563eb] text-white text-sm font-semibold px-4 py-2 rounded">Generate</button>
        </form>
        {newKey && (
          <div className="mt-4 rounded border border-emerald-300 bg-emerald-50 p-3 text-sm">
            <div className="font-semibold text-emerald-900">Your new API key — copy it now, we don't store it in plain text:</div>
            <div className="mt-2 flex items-center gap-2 font-mono text-xs bg-white border rounded px-2 py-1">
              <code className="flex-1 break-all">{newKey.key}</code>
              <button onClick={() => copyKey(newKey.key)} className="text-[#2563eb] font-semibold"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        )}
      </div>
      <div className="border border-[#e2e8f0] rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <tr><th className="text-left px-4 py-2.5">Name</th><th className="text-left px-4 py-2.5">Prefix</th><th className="text-left px-4 py-2.5">Last used</th><th className="text-left px-4 py-2.5">Status</th><th /></tr>
          </thead>
          <tbody>
            {list.map((k) => (
              <tr key={k.id} className="border-b border-[#e2e8f0] last:border-0">
                <td className="px-4 py-3 font-medium">{k.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{k.prefix}…</td>
                <td className="px-4 py-3 text-xs text-[#64748b]">{formatDate(k.last_used_at)}</td>
                <td className="px-4 py-3 text-xs">{k.revoked ? <span className="text-red-600 font-semibold">Revoked</span> : <span className="text-emerald-600 font-semibold">Active</span>}</td>
                <td className="px-4 py-3 text-right">{!k.revoked && <button onClick={() => revoke(k.id)} className="text-red-600 text-xs font-semibold">Revoke</button>}</td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-[#64748b]">No API keys yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <ApiReferenceDoc />
    </div>
  );
}

function ApiReferenceDoc() {
  const base = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
  return (
    <div className="mt-8 rounded-lg border border-[#e2e8f0] bg-white p-5">
      <h3 className="font-display font-bold text-lg text-[#0f1b2d]">API reference (paste into Make.com / AI agent)</h3>
      <div className="mt-3 text-xs text-[#475569] space-y-3">
        <div>
          <div className="font-semibold text-[#0f1b2d]">Base URL</div>
          <code className="block bg-[#f8fafc] border border-[#e2e8f0] rounded px-2 py-1 mt-1 font-mono">{base}</code>
        </div>
        <div>
          <div className="font-semibold text-[#0f1b2d]">Authentication</div>
          <div>Send header <code>X-API-Key: sfk_...</code> on every request.</div>
        </div>
        <div>
          <div className="font-semibold text-[#0f1b2d]">Endpoints</div>
          <ul className="mt-1 space-y-1 font-mono text-[11px]">
            <li>GET  /content/blog-posts</li>
            <li>POST /content/blog-posts</li>
            <li>GET  /content/blog-posts/{`{id}`}</li>
            <li>PATCH /content/blog-posts/{`{id}`}</li>
            <li>GET  /content/guide/combinations</li>
            <li>POST /content/guide/combinations</li>
            <li>GET  /content/guide/{`{dest}`}/{`{nat}`}</li>
            <li>PATCH /content/guide/{`{dest}`}/{`{nat}`}</li>
          </ul>
        </div>
        <div>
          <div className="font-semibold text-[#0f1b2d]">Blog POST body example</div>
          <pre className="bg-[#0f1b2d] text-[#e2e8f0] rounded p-3 mt-1 overflow-x-auto text-[11px]">{JSON.stringify({
            title: "Understanding Schengen refusal codes",
            slug: "understanding-schengen-refusal-codes",
            category: "application_process",
            excerpt: "The 15 refusal codes on your rejection letter, decoded.",
            meta_description: "Understand each Schengen refusal code and what to do next.",
            status: "published",
            body: [
              { type: "paragraph", text: "The consulate returns one of 15 standard codes…" },
              { type: "heading", level: 2, text: "Code 1 — Purpose not established" },
            ],
          }, null, 2)}</pre>
        </div>
        <div>
          <div className="font-semibold text-[#0f1b2d]">Guide PATCH body example</div>
          <pre className="bg-[#0f1b2d] text-[#e2e8f0] rounded p-3 mt-1 overflow-x-auto text-[11px]">{JSON.stringify({
            overrides: {
              overview: { text: "Updated narrative overview…", bullets: ["…"] },
              faqs: [{ q: "…", a: "…" }],
            },
            status: "published",
          }, null, 2)}</pre>
        </div>
      </div>
    </div>
  );
}

export function AdminContentWebhooks() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ url: "", events: ["post.published"], active: true, secret: "" });
  const [showNew, setShowNew] = useState(false);
  const load = () => api.get("/admin/content/webhooks").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const toggleEvent = (e) => setForm({ ...form, events: form.events.includes(e) ? form.events.filter((x) => x !== e) : [...form.events, e] });
  const save = async (e) => {
    e.preventDefault();
    await api.post("/admin/content/webhooks", form);
    setForm({ url: "", events: ["post.published"], active: true, secret: "" }); setShowNew(false); load();
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/content/webhooks/${id}`); load(); };
  return (
    <div data-testid="admin-webhooks-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-bold text-xl text-[#0f1b2d]">Outbound webhooks</h2>
        <button onClick={() => setShowNew(!showNew)} className="inline-flex items-center gap-2 bg-[#2563eb] text-white text-sm font-semibold px-3.5 py-2 rounded"><Plus className="w-4 h-4" />Add</button>
      </div>
      {showNew && (
        <form onSubmit={save} className="mb-6 p-4 border border-[#e2e8f0] rounded-lg bg-white space-y-3">
          <input required placeholder="Webhook URL (Make.com, Zapier, custom)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="w-full border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          <div className="flex flex-wrap gap-2">
            {["post.published", "guide.updated", "content.pending_review"].map((e) => (
              <label key={e} className="text-xs inline-flex items-center gap-1.5 border border-[#e2e8f0] rounded px-2.5 py-1.5">
                <input type="checkbox" checked={form.events.includes(e)} onChange={() => toggleEvent(e)} /> {e}
              </label>
            ))}
          </div>
          <input placeholder="HMAC secret (optional)" value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })} className="w-full border border-[#e2e8f0] rounded px-3 py-2 text-sm" />
          <div className="flex gap-2"><button type="submit" className="bg-[#0f1b2d] text-white text-sm font-semibold px-4 py-1.5 rounded">Add</button><button type="button" onClick={() => setShowNew(false)} className="border rounded px-4 text-sm">Cancel</button></div>
        </form>
      )}
      <div className="border border-[#e2e8f0] rounded-lg bg-white overflow-hidden">
        {list.length === 0 ? <div className="p-6 text-center text-[#64748b] text-sm">No webhooks yet.</div> : list.map((h) => (
          <div key={h.id} className="border-b border-[#e2e8f0] last:border-0 p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-xs truncate">{h.url}</div>
              <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                {h.events.map((e) => <span key={e} className="bg-[#f1f5f9] rounded px-1.5 py-0.5">{e}</span>)}
              </div>
            </div>
            <div className="text-xs text-[#64748b]">
              {h.active ? <span className="text-emerald-600 font-semibold">Active</span> : <span className="text-[#94a3b8]">Off</span>}
              <button onClick={() => remove(h.id)} className="ml-3 text-red-600 text-xs font-semibold">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminContentSettings() {
  const [s, setS] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [topic, setTopic] = useState("");
  useEffect(() => { api.get("/admin/content/settings").then((r) => setS(r.data)); }, []);
  const save = async () => {
    await api.patch("/admin/content/settings", s);
    toast.success("Settings saved");
  };
  const generateNow = async () => {
    setGenerating(true);
    try {
      const brief = (topic || "").trim();
      const r = await api.post("/admin/content/blog/auto-generate",
                               brief ? { topic: brief } : {});
      if (r.data?.status === "queued") {
        toast.success(brief
          ? "Generation started with your brief — check the Activity Log in 1–2 minutes."
          : "Generation started — check the Activity Log in 1–2 minutes.");
        setTopic("");
      } else if (r.data?.status === "success") {
        toast.success(`Published: ${r.data.title || r.data.slug}`);
        setTopic("");
      } else {
        toast.error(`Auto-generate failed: ${r.data?.error || "unknown error"}`);
      }
    } catch (e) {
      toast.error(`Auto-generate failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setGenerating(false);
    }
  };
  if (!s) return <div className="text-sm text-[#64748b]">Loading…</div>;
  return (
    <div data-testid="admin-content-settings-page">
      <h2 className="font-display font-bold text-xl text-[#0f1b2d] mb-6">Content automation settings</h2>
      <div className="rounded-lg border border-[#e2e8f0] bg-white p-6 space-y-4 max-w-2xl">
        <Toggle label="Blog auto-publish (API submissions)" desc="When ON, blog posts submitted via the Content API with status=published go live immediately. When OFF, they land in the Review Queue." value={s.blog_auto_publish} onChange={(v) => setS({ ...s, blog_auto_publish: v })} />
        <Toggle label="Guide auto-publish (API submissions)" desc="When ON, guide narrative content submitted via the API is applied immediately." value={s.guide_auto_publish} onChange={(v) => setS({ ...s, guide_auto_publish: v })} />
        <Toggle label="Sensitive-field verification" desc="Numeric/factual guide fields (fees, processing, appointment center) submitted via the API are held pending until manually approved. Recommended ON." value={s.sensitive_verification_enabled} onChange={(v) => setS({ ...s, sensitive_verification_enabled: v })} />
        <div className="pt-4 border-t border-[#e2e8f0]">
          <Toggle
            label="Daily auto-blog"
            desc="When ON, auto-generates and publishes one SEO+GEO-optimized blog post per day at 06:00 UTC (09:00 Riyadh) using Claude Sonnet 4.6 + Pexels photos. OFF by default — flip this on to resume the daily schedule. Manual 'Generate post now' works regardless of this toggle."
            value={!!s.daily_auto_blog}
            onChange={(v) => setS({ ...s, daily_auto_blog: v })}
          />
          <div className="mt-4">
            <label className="block text-sm font-medium text-[#0f1b2d] mb-1.5" htmlFor="content-settings-topic">
              Topic / instructions <span className="text-[#94a3b8] font-normal">(optional)</span>
            </label>
            <textarea
              id="content-settings-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              rows={4}
              placeholder="e.g. 'Cover letter samples for US H1B holders applying for a Schengen visa — focus on financial evidence and typical wording. Around 1800 words. Include 4-5 FAQs.' Leave empty to let the AI pick a fresh long-tail topic."
              data-testid="content-settings-topic-input"
              className="w-full text-sm border border-[#cbd5e1] rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb] placeholder-[#94a3b8] resize-y"
            />
            <div className="text-xs text-[#64748b] mt-1.5">
              When provided, the AI writes to your brief. Empty box → auto-picked long-tail topic (previous behaviour). Duplicate-title avoidance always applies.
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button
              onClick={generateNow}
              disabled={generating}
              data-testid="content-settings-generate-now"
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white text-sm font-semibold px-4 py-2 rounded"
            >
              {generating ? "Generating…" : "Generate post now"}
            </button>
            <span className="text-xs text-[#64748b]">Bypasses the daily schedule and toggle. Publishes immediately.</span>
          </div>
        </div>
        <button onClick={save} data-testid="content-settings-save" className="bg-[#2563eb] text-white text-sm font-semibold px-4 py-2 rounded">Save</button>
      </div>
    </div>
  );
}
function Toggle({ label, desc, value, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="font-medium text-[#0f1b2d]">{label}</div>
        <div className="text-xs text-[#64748b] max-w-lg">{desc}</div>
      </div>
      <button onClick={() => onChange(!value)} className={`w-11 h-6 rounded-full transition ${value ? "bg-[#2563eb]" : "bg-[#cbd5e1]"} relative flex-shrink-0`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${value ? "translate-x-5" : ""}`} />
      </button>
    </div>
  );
}

export function AdminContentReviewQueue() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/content/review-queue").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const decide = async (id, action) => { await api.post(`/admin/content/review-queue/${id}/decide`, { action }); load(); toast.success(`${action}ed`); };
  const bulk = async () => { await api.post("/admin/content/review-queue/bulk-approve"); load(); toast.success("Bulk approved"); };
  return (
    <div data-testid="admin-review-queue-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-bold text-xl text-[#0f1b2d]">Review queue</h2>
        {list.length > 0 && <button onClick={bulk} className="text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded">Approve all</button>}
      </div>
      {list.length === 0 ? (
        <div className="rounded-lg border border-[#e2e8f0] bg-white p-6 text-center text-sm text-[#64748b]">The review queue is empty.</div>
      ) : (
        <div className="space-y-3">
          {list.map((item) => (
            <div key={item.id} className="rounded-lg border border-amber-300 bg-amber-50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-amber-900">{item.content_type} · {item.action}</div>
                  <div className="mt-1 text-sm font-medium text-[#0f1b2d]">{item.payload?.title || item.target_id}</div>
                  <div className="text-xs text-[#64748b]">Submitted {formatDate(item.created_at)}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => decide(item.id, "approve")} className="text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded">Approve</button>
                  <button onClick={() => decide(item.id, "reject")} className="text-sm font-semibold bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded">Reject</button>
                </div>
              </div>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-[#475569] font-semibold">View payload</summary>
                <pre className="mt-2 text-[11px] bg-white border border-amber-200 rounded p-2 overflow-x-auto">{JSON.stringify(item.payload, null, 2)}</pre>
              </details>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminContentActivity() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/content/activity").then((r) => setList(r.data)); }, []);
  return (
    <div data-testid="admin-activity-page">
      <h2 className="font-display font-bold text-xl text-[#0f1b2d] mb-6">AI Content activity</h2>
      <div className="border border-[#e2e8f0] rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <tr><th className="text-left px-4 py-2.5">Time</th><th className="text-left px-4 py-2.5">Action</th><th className="text-left px-4 py-2.5">Type</th><th className="text-left px-4 py-2.5">Target</th><th className="text-left px-4 py-2.5">Key</th><th className="text-left px-4 py-2.5">Status</th></tr>
          </thead>
          <tbody>
            {list.map((l) => (
              <tr key={l.id} className="border-b border-[#e2e8f0] last:border-0">
                <td className="px-4 py-2 text-xs text-[#64748b]">{formatDate(l.timestamp, true)}</td>
                <td className="px-4 py-2 text-xs">{l.action}</td>
                <td className="px-4 py-2 text-xs">{l.content_type}</td>
                <td className="px-4 py-2 text-xs font-mono">{l.target_id}</td>
                <td className="px-4 py-2 text-xs">{l.api_key_name || "—"}</td>
                <td className="px-4 py-2 text-xs">{l.status}</td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#64748b]">No activity yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// Utilities
// ============================================================
function formatDate(iso, showTime = false) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (showTime) return d.toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}
